import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { Permission } from '../../constants/permissions';

// Whether a gate may be answered yet. `loading` is not "denied": rendering a
// page as forbidden before its permissions land would bounce a user who is in
// fact allowed in, so every caller has to distinguish the two.
export const PERMISSION_STATUSES = {
  loading: 'loading',
  granted: 'granted',
  denied: 'denied'
} as const;

export type PermissionStatus = (typeof PERMISSION_STATUSES)[keyof typeof PERMISSION_STATUSES];

interface PermissionsState {
  // Which location `locationPermissions` describes. Kept alongside the set so a
  // gate can tell "you may not do this here" from "these are last location's
  // permissions and the new ones have not arrived".
  locationId: string | null;
  locationPermissions: Permission[];
  isLocationLoaded: boolean;
  accountPermissions: Permission[];
  isAccountLoaded: boolean;
}

const initialState: PermissionsState = {
  locationId: null,
  locationPermissions: [],
  isLocationLoaded: false,
  accountPermissions: [],
  isAccountLoaded: false
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    setLocationPermissions: (
      state,
      action: PayloadAction<{ locationId: string; permissions: Permission[] }>
    ) => {
      state.locationId = action.payload.locationId;
      state.locationPermissions = action.payload.permissions;
      state.isLocationLoaded = true;
    },
    clearLocationPermissions: (state) => {
      state.locationId = null;
      state.locationPermissions = [];
      state.isLocationLoaded = false;
    },
    setAccountPermissions: (state, action: PayloadAction<{ permissions: Permission[] }>) => {
      state.accountPermissions = action.payload.permissions;
      state.isAccountLoaded = true;
    },
    clearAccountPermissions: (state) => {
      state.accountPermissions = [];
      state.isAccountLoaded = false;
    }
  }
});

export const {
  setLocationPermissions,
  clearLocationPermissions,
  setAccountPermissions,
  clearAccountPermissions
} = permissionsSlice.actions;

/**
 * Whether the signed-in user holds `permission` at `locationId`.
 *
 * Answers false — never true — while the permissions are still loading or
 * belong to another location, so a control is hidden until it is known to be
 * allowed rather than flashing into view and being taken away.
 */
export const selectHasLocationPermission =
  (permission: Permission, locationId: string | null) =>
  (state: RootState): boolean =>
    state.permissionsSlice.isLocationLoaded &&
    state.permissionsSlice.locationId === locationId &&
    state.permissionsSlice.locationPermissions.includes(permission);

// Same question against whichever location was last fetched. For controls that
// are rendered inside a location's own page, where the store cannot be
// describing anything else.
export const selectHasActiveLocationPermission =
  (permission: Permission) =>
  (state: RootState): boolean =>
    state.permissionsSlice.isLocationLoaded &&
    state.permissionsSlice.locationPermissions.includes(permission);

export const selectHasAccountPermission =
  (permission: Permission) =>
  (state: RootState): boolean =>
    state.permissionsSlice.isAccountLoaded &&
    state.permissionsSlice.accountPermissions.includes(permission);

export const selectLocationPermissionStatus =
  (permission: Permission, locationId: string | null) =>
  (state: RootState): PermissionStatus => {
    if (!state.permissionsSlice.isLocationLoaded || state.permissionsSlice.locationId !== locationId)
      return PERMISSION_STATUSES.loading;
    return state.permissionsSlice.locationPermissions.includes(permission)
      ? PERMISSION_STATUSES.granted
      : PERMISSION_STATUSES.denied;
  };

export const selectAccountPermissionStatus =
  (permission: Permission) =>
  (state: RootState): PermissionStatus => {
    if (!state.permissionsSlice.isAccountLoaded) return PERMISSION_STATUSES.loading;
    return state.permissionsSlice.accountPermissions.includes(permission)
      ? PERMISSION_STATUSES.granted
      : PERMISSION_STATUSES.denied;
  };

// Which location the stored location permissions describe. Needed by anything
// that has to re-read them: the store is the only thing that knows which
// location's answer is currently in it.
export const selectPermissionsLocationId = (state: RootState): string | null =>
  state.permissionsSlice.locationId;

export const selectAreAccountPermissionsLoaded = (state: RootState): boolean =>
  state.permissionsSlice.isAccountLoaded;

export const selectAreLocationPermissionsLoaded = (state: RootState): boolean =>
  state.permissionsSlice.isLocationLoaded;

export default permissionsSlice.reducer;
